<template>
  <!-- User Menu Overlay -->
  <div v-if="isOpen" class="menu-overlay" @click="close"></div>
  <div class="user-menu" :class="{ open: isOpen }">
    <div class="menu-header">
      <span class="user-name">{{ currentUser?.name }}</span>
      <button class="btn-close-menu" @click="close" aria-label="Close menu">
        <i class="bi bi-x-lg"></i>
      </button>
    </div>
    <div class="menu-content">
      <!-- Language selection -->
      <div class="menu-item" @click="toggleLanguageSubmenu">
        <i class="bi bi-translate me-3"></i>
        <span>{{ $t('common.language') }}</span>
        <i class="bi bi-chevron-down ms-auto" :class="{ rotated: isLanguageSubmenuOpen }"></i>
      </div>
      <div class="submenu" :class="{ show: isLanguageSubmenuOpen }">
        <a class="submenu-item" href="#" @click="setLanguage('ja')">🇯🇵 日本語</a>
        <a class="submenu-item" href="#" @click="setLanguage('en')">🇺🇸 English</a>
        <a class="submenu-item" href="#" @click="setLanguage('vi')">🇻🇳 Tiếng Việt</a>
        <a class="submenu-item" href="#" @click="setLanguage('th')">🇹🇭 ไทย</a>
      </div>

      <!-- Change email address -->
      <div class="menu-item" @click="changeEmailAddress">
        <i class="bi bi-envelope me-3"></i>
        <span>{{ $t('email.change') }}</span>
      </div>

      <!-- Change password -->
      <div class="menu-item" @click="changePassword">
        <i class="bi bi-key me-3"></i>
        <span>{{ $t('password.change') }}</span>
      </div>

      <!-- Logout -->
      <div class="menu-item" @click="logout">
        <i class="bi bi-box-arrow-right me-3"></i>
        <span>{{ $t('common.logout') }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthUser } from '../../../composables/useAuthUser.js';
import { setLocale } from '../../../plugins/i18n.js';
import { colors } from '../../../../consts/colors';

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  currentUser: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close']);

const router = useRouter();
const { logout: authLogout } = useAuthUser();
const isLanguageSubmenuOpen = ref(false);

/**
 * Close menu
 */
const close = () => {
  isLanguageSubmenuOpen.value = false;
  emit('close');
};

/**
 * Toggle language submenu
 */
const toggleLanguageSubmenu = () => {
  isLanguageSubmenuOpen.value = !isLanguageSubmenuOpen.value;
};

/**
 * Set application language
 */
const setLanguage = (locale) => {
  setLocale(locale);
  close();
};

/**
 * Navigate to change password page
 */
const changeEmailAddress = () => {
  close();
  router.push({ name: 'change-email-address' });
};

/**
 * Navigate to change password page
 */
const changePassword = () => {
  close();
  router.push({ name: 'change-password' });
};

/**
 * Logout user
 */
const logout = () => {
  close();
  authLogout();
};
</script>

<style lang="scss" scoped>
// Menu overlay
.menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: v-bind('colors.overlayBg');
  z-index: 1040;
  display: block;
}

// User menu (slides from right, full height for both mobile and desktop)
.user-menu {
  position: fixed;
  top: 0;
  right: -100%;
  width: 80%;
  max-width: 320px;
  height: 100vh;
  background-color: v-bind('colors.white');
  box-shadow: -2px 0 8px v-bind('colors.shadowColor');
  transition: right 0.3s ease-in-out;
  z-index: 1050;
  overflow-y: auto;

  &.open {
    right: 0;
  }

  .menu-header {
    padding: 16px 24px;
    border-bottom: 1px solid v-bind('colors.gray[200]');
    display: flex;
    justify-content: space-between;
    align-items: center;

    .user-name {
      color: v-bind('colors.textPrimary');
      font-size: 16px;
      font-weight: 600;
    }

    .btn-close-menu {
      background: none;
      border: none;
      padding: 8px;
      cursor: pointer;
      color: v-bind('colors.textPrimary');
      font-size: 20px;
      transition: color 0.2s;

      &:hover {
        color: v-bind('colors.blue[700]');
      }

      &:focus {
        outline: none;
      }
    }
  }

  .menu-content {
    padding: 0;
  }

  .menu-item {
    padding: 16px 24px;
    display: flex;
    align-items: center;
    color: v-bind('colors.textPrimary');
    font-size: 14px;
    font-weight: 400;
    cursor: pointer;
    transition: background-color 0.2s;
    border-bottom: 1px solid v-bind('colors.background');

    &:hover {
      background-color: v-bind('colors.background');
    }

    i {
      font-size: 16px;
    }

    .bi-chevron-down {
      font-size: 12px;
      transition: transform 0.2s;

      &.rotated {
        transform: rotate(180deg);
      }
    }
  }

  .submenu {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease;
    background-color: v-bind('colors.gray[50]');

    &.show {
      max-height: 300px;
    }
  }

  .submenu-item {
    padding: 12px 24px 12px 48px;
    display: block;
    color: v-bind('colors.textPrimary');
    font-size: 14px;
    text-decoration: none;
    transition: background-color 0.2s;

    &:hover {
      background-color: v-bind('colors.gray[200]');
    }
  }
}
</style>
